import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, FileText, MapPin, Mail, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Terms of Service content
const TermsContent = () => (
  <div className="prose prose-sm max-w-none text-gray-600">
    <h3 className="text-lg font-semibold text-gray-900">Termini e Condizioni di Utilizzo</h3>
    <p className="text-sm">Ultimo aggiornamento: Gennaio 2026</p>
    
    <h4 className="font-semibold mt-4">1. Accettazione dei Termini</h4>
    <p>Utilizzando l&apos;applicazione ADE, accetti di essere vincolato da questi Termini di Servizio.</p>
    
    <h4 className="font-semibold mt-4">2. Descrizione del Servizio</h4>
    <p>ADE è un&apos;applicazione di social networking basata sulla prossimità che permette agli utenti di connettersi con persone nelle vicinanze.</p>
    
    <h4 className="font-semibold mt-4">3. Account Utente</h4>
    <ul className="list-disc pl-5">
      <li>Devi avere almeno 18 anni per utilizzare ADE</li>
      <li>Sei responsabile della sicurezza del tuo account</li>
      <li>Le informazioni fornite devono essere accurate</li>
    </ul>
    
    <h4 className="font-semibold mt-4">4. Comportamento</h4>
    <p>Ti impegni a non molestare altri utenti, pubblicare contenuti illegali o creare profili falsi.</p>
  </div>
);

// Privacy Policy content
const PrivacyContent = () => (
  <div className="prose prose-sm max-w-none text-gray-600">
    <h3 className="text-lg font-semibold text-gray-900">Informativa sulla Privacy (GDPR)</h3>
    <p className="text-sm">Ultimo aggiornamento: Gennaio 2026</p>
    
    <h4 className="font-semibold mt-4">Dati Raccolti</h4>
    <ul className="list-disc pl-5">
      <li><strong>Dati di registrazione:</strong> nome, email, foto profilo</li>
      <li><strong>Dati di geolocalizzazione:</strong> posizione GPS in tempo reale</li>
      <li><strong>Dati di utilizzo:</strong> interazioni, messaggi, connessioni</li>
    </ul>
    
    <h4 className="font-semibold mt-4">Finalità</h4>
    <p>I dati sono utilizzati per fornire il servizio di social networking e permettere la rilevazione di utenti nelle vicinanze.</p>
    
    <h4 className="font-semibold mt-4">I Tuoi Diritti</h4>
    <p>Hai diritto ad accedere, rettificare, cancellare i tuoi dati e opporti al trattamento.</p>
  </div>
);

// Location consent content
const LocationContent = () => (
  <div className="prose prose-sm max-w-none text-gray-600">
    <h3 className="text-lg font-semibold text-gray-900">Consenso alla Geolocalizzazione</h3>
    
    <h4 className="font-semibold mt-4">Perché raccogliamo la tua posizione</h4>
    <ul className="list-disc pl-5">
      <li>Mostrarti utenti nelle tue vicinanze</li>
      <li>Permettere ad altri utenti di trovarti</li>
      <li>Calcolare le distanze tra utenti</li>
    </ul>
    
    <h4 className="font-semibold mt-4">Storico Posizioni</h4>
    <p>Conserviamo lo storico delle posizioni per massimo 24 ore per motivi di sicurezza. Puoi disattivare il GPS in qualsiasi momento.</p>
  </div>
);

export default function PrivacyConsentPrompt({ user, onConsentsAccepted }) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [locationConsent, setLocationConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  
  const canSubmit = termsAccepted && privacyAccepted && locationConsent;
  
  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Devi accettare tutti i consensi obbligatori");
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/privacy/consents', {
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted,
        location_consent: locationConsent,
        marketing_consent: marketingConsent
      });
      toast.success("Consensi salvati!");
      onConsentsAccepted();
    } catch (error) {
      toast.error("Errore nel salvataggio dei consensi");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-orange-50/30 to-pink-50/30 flex flex-col" data-testid="privacy-consent-prompt">
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Aggiornamento Privacy</h2>
            <p className="text-gray-500 text-sm text-center mt-2">
              Ciao {user?.name}! Abbiamo aggiornato i nostri termini.<br />
              Per continuare a utilizzare ADE, accetta i nuovi consensi.
            </p>
          </div>
          
          {/* Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Questi consensi sono necessari per la conformità GDPR e per garantire la tua privacy e sicurezza.
            </p>
          </div>
          
          {/* Consent cards */}
          <div className="space-y-3">
            {/* Terms of Service */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                termsAccepted ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-1"
                  data-testid="terms-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="terms" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-500" />
                    Termini e Condizioni *
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Accetto i termini di utilizzo del servizio ADE
                  </p>
                  <button
                    onClick={() => setShowTerms(true)}
                    className="text-xs text-pink-500 hover:text-pink-600 mt-1 underline"
                  >
                    Leggi i Termini e Condizioni
                  </button>
                </div>
                {termsAccepted && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
            
            {/* Privacy Policy */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                privacyAccepted ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  className="mt-1"
                  data-testid="privacy-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="privacy" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-500" />
                    Privacy Policy *
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Acconsento al trattamento dei miei dati personali secondo GDPR
                  </p>
                  <button
                    onClick={() => setShowPrivacy(true)}
                    className="text-xs text-pink-500 hover:text-pink-600 mt-1 underline"
                  >
                    Leggi l&apos;Informativa Privacy
                  </button>
                </div>
                {privacyAccepted && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
            
            {/* Location consent */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                locationConsent ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="location"
                  checked={locationConsent}
                  onCheckedChange={setLocationConsent}
                  className="mt-1"
                  data-testid="location-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="location" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    Geolocalizzazione *
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Acconsento alla raccolta e utilizzo della mia posizione GPS
                  </p>
                  <button
                    onClick={() => setShowLocation(true)}
                    className="text-xs text-pink-500 hover:text-pink-600 mt-1 underline"
                  >
                    Maggiori informazioni
                  </button>
                </div>
                {locationConsent && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
            
            {/* Marketing consent (optional) */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                marketingConsent ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="marketing"
                  checked={marketingConsent}
                  onCheckedChange={setMarketingConsent}
                  className="mt-1"
                  data-testid="marketing-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="marketing" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    Comunicazioni Marketing
                    <span className="text-xs text-gray-400 font-normal">(opzionale)</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Voglio ricevere novità, offerte e aggiornamenti su ADE
                  </p>
                </div>
                {marketingConsent && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-400 text-center mt-4">
            * Consenso obbligatorio per continuare a utilizzare ADE
          </p>
          
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white text-lg font-semibold hover:opacity-90 shadow-xl shadow-pink-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            data-testid="accept-consents-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Salvataggio...
              </span>
            ) : (
              <>
                Accetta e Continua
                <Check className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Terms Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Termini e Condizioni</DialogTitle>
          </DialogHeader>
          <TermsContent />
          <Button onClick={() => { setTermsAccepted(true); setShowTerms(false); }} className="w-full mt-4">
            Accetto i Termini
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Privacy Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Informativa Privacy</DialogTitle>
          </DialogHeader>
          <PrivacyContent />
          <Button onClick={() => { setPrivacyAccepted(true); setShowPrivacy(false); }} className="w-full mt-4">
            Accetto la Privacy Policy
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Location Dialog */}
      <Dialog open={showLocation} onOpenChange={setShowLocation}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consenso Geolocalizzazione</DialogTitle>
          </DialogHeader>
          <LocationContent />
          <Button onClick={() => { setLocationConsent(true); setShowLocation(false); }} className="w-full mt-4">
            Acconsento alla Geolocalizzazione
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
